import * as Dfns from 'date-fns/fp';
import { ungzip } from 'pako';
import { ValueLabelRecord } from '@blisspointmedia/bpm-types/dist/App2Performance';
import { base64ToUint8Array } from '@blisspointmedia/bpm-types/dist/binaryUtils';
import {
  BpmDateString,
  CompanyFiscalCal,
  DateGroupingKey,
  FiscalCalType,
  FiscalPatternCal,
  getMonthLabelPattern,
  getQuarterLabelPattern,
  getQuarterLabelYearly,
  getYearLabelPattern,
  getYearLabelYearly,
  OUTSIDE_RANGE_ERR_MSG
} from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import { convertDateToUTC } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { DateInfo } from '@blisspointmedia/bpm-types/dist/MetricsPage';
import {
  computeCustomRelativeRange,
  computeDateFromComparisonPreset,
  computeDateFromPreset
} from '@blisspointmedia/bpm-types/dist/RelativeDatePresets';

export interface DateRange {
  start: string;
  end: string;
}

type ComparisonFiscalCal = Parameters<typeof computeDateFromComparisonPreset>[3];

/**
 * The default comparison for a primary range: the previous period, or the previous month/quarter/
 * year when the primary is whole fiscal months. Undefined if it can't be derived.
 */
export const deriveDefaultComparisonRange = (
  primary: DateRange,
  fiscalCal: ComparisonFiscalCal
): DateRange | undefined =>
  computeDateFromComparisonPreset('Default', primary.start, primary.end, fiscalCal);

/**
 * `candidate` (e.g. a remembered comparison range) when it ends before `primary` starts, otherwise
 * the default comparison derived from `primary`. The candidate is the last resort if nothing can
 * be derived.
 */
export const comparisonRangeBefore = (
  primary: DateRange,
  candidate: DateRange,
  fiscalCal: ComparisonFiscalCal
): DateRange =>
  candidate.end < primary.start
    ? candidate
    : (deriveDefaultComparisonRange(primary, fiscalCal) ?? candidate);

/**
 * Resolves a page preset's saved date info to concrete ranges. `primary` is undefined when the
 * preset has no usable primary dates. `comparison` falls back to the default comparison derived
 * from `primary` (a previous period, rather than the global default, which can land after the
 * primary), then to `fallbackComparison`.
 */
export const resolvePresetDateInfo = (
  dateInfo: DateInfo,
  fiscalCal: ComparisonFiscalCal,
  fallbackComparison: DateRange
): { primary: DateRange | undefined; comparison: DateRange } => {
  const {
    start,
    end,
    dateType,
    relative_date_info,
    comparison_start,
    comparison_end,
    comparison_dateType,
    comparison_relative_date_info
  } = dateInfo;
  let primary: DateRange | undefined;
  let comparison: DateRange | undefined;
  if (dateType === 'relative' && relative_date_info && relative_date_info !== 'Custom') {
    try {
      primary =
        typeof relative_date_info === 'string'
          ? computeDateFromPreset(relative_date_info, fiscalCal)
          : computeCustomRelativeRange(relative_date_info.start, relative_date_info.end, fiscalCal);
    } catch {
      console.error(OUTSIDE_RANGE_ERR_MSG);
    }
  } else if (start && end) {
    primary = { start, end };
  }
  if (
    comparison_dateType === 'relative' &&
    comparison_relative_date_info &&
    comparison_relative_date_info !== 'Custom' &&
    primary
  ) {
    try {
      comparison =
        typeof comparison_relative_date_info === 'string'
          ? computeDateFromComparisonPreset(
              comparison_relative_date_info,
              primary.start,
              primary.end,
              fiscalCal
            )
          : computeCustomRelativeRange(
              comparison_relative_date_info.start,
              comparison_relative_date_info.end,
              fiscalCal
            );
    } catch {
      console.error(OUTSIDE_RANGE_ERR_MSG);
    }
  } else if (comparison_start && comparison_end) {
    comparison = { start: comparison_start, end: comparison_end };
  }
  return {
    primary,
    comparison:
      comparison ??
      (primary ? deriveDefaultComparisonRange(primary, fiscalCal) : undefined) ??
      fallbackComparison
  };
};

export interface DateGroupingOption {
  value: DateGroupingKey;
  label: string;
}

export interface DayOfWeekOption {
  key: string;
  label: string;
  number: number;
}

export const DAY_OF_WEEK_OPTIONS: DayOfWeekOption[] = [
  { key: 'Sunday', label: 'S', number: 0 },
  { key: 'Monday', label: 'M', number: 1 },
  { key: 'Tuesday', label: 'T', number: 2 },
  { key: 'Wednesday', label: 'W', number: 3 },
  { key: 'Thursday', label: 'T', number: 4 },
  { key: 'Friday', label: 'F', number: 5 },
  { key: 'Saturday', label: 'S', number: 6 }
];

export const DATE_GROUPING_OPTIONS: DateGroupingOption[] = [
  { value: 'Day', label: 'Daily' },
  { value: 'Week', label: 'Weekly' },
  { value: 'Month', label: 'Monthly' },
  { value: 'Quarter', label: 'Quarterly' },
  { value: 'Year', label: 'Yearly' }
];

export const getDefaultDates = (data: { date: string }[]): DateRange => ({
  start: data[0].date,
  end: data[data.length - 1].date
});

export const DAY_DATE_LABEL = 'M/dd/yy';
export const WEEK_DATE_LABEL = 'MM/dd';
export const MONTH_DATE_LABEL = 'MMM yyyy';
export const QUARTER_DATE_LABEL = 'QQQ yyyy';
export const YEAR_DATE_LABEL = 'yyyy';
export const DAY_DATE_TOOLTIP_LABEL = 'MMMM do, yyyy';
export const MONTH_DATE_TOOLTIP_LABEL = 'MMMM yyyy';

export const snapToMonday = (date: string): string =>
  Dfns.format(DATE_FORMAT, Dfns.startOfISOWeek(Dfns.parseISO(date)));

// DatePicker's isDayBlocked predicate for full-weeks-only pickers that should only allow
// selecting the Monday of each week.
export const isNotMonday = (date: string): boolean => Dfns.getDay(Dfns.parseISO(date)) !== 1;

export const MILLISECONDS_IN_A_DAY = 1000 * 60 * 60 * 24;
export const SHORT_DATE_FORMAT = 'MM/dd';
export const DATE_FORMAT = 'yyyy-MM-dd';
export const DAYS_OF_WEEK = ['M', 'Tu', 'W', 'Th', 'F', 'Sa', 'Su'] as const;
export const CURRENT_WEEK_START = Dfns.format(DATE_FORMAT, Dfns.startOfISOWeek(new Date()));
export const CURRENT_WEEK_END = Dfns.format(DATE_FORMAT, Dfns.endOfISOWeek(new Date()));

/**
 * Human-friendly day label like "Friday, August 21st" — includes the year only when the date
 * isn't in the current year (e.g. "Friday, August 21st, 2024").
 *
 * Accepts `Date | string`; ISO date-only strings ("2024-08-21") are parsed through `parseISO`
 * so they land on the local calendar day rather than UTC midnight (which would shift them into
 * the previous day in negative-offset timezones).
 *
 * @param date - The date to label.
 * @param now  - "Current" reference date; defaults to `new Date()`. Injected for testability.
 */
// date-fns/fp reverses args: differenceInCalendarDays(earlier)(later) === later - earlier.
export const calendarDaysAgo = (date: Date, now: Date): number =>
  Dfns.differenceInCalendarDays(Dfns.startOfDay(date))(Dfns.startOfDay(now));

export const formatFriendlyDay = (date: Date | string, now: Date = new Date()): string => {
  const parsed = typeof date === 'string' ? Dfns.parseISO(date) : date;
  const sameYear = Dfns.getYear(parsed) === Dfns.getYear(now);
  return Dfns.format(sameYear ? 'EEEE, MMMM do' : 'EEEE, MMMM do, yyyy', parsed);
};

// Short weekday + month label like "Fri, Aug 21".
export const formatDayMonth = (date: Date | string): string =>
  Dfns.format('EEE, MMM d', typeof date === 'string' ? Dfns.parseISO(date) : date);

/**
 * Human-friendly timestamp for surfaces that show a specific moment (comment posted-at,
 * message received-at). Prefers "Today" / "Yesterday" over calendar dates when the moment
 * lands in the local reference-day window, and includes the time. Older-than-yesterday
 * timestamps fall back to a month/day (+ year if it's not the current year).
 *
 * Uses startOfDay + differenceInCalendarDays so the today/yesterday cutoffs respect the local
 * calendar day rather than a UTC-24h-window (same defensiveness as ActivityFeed's date-group
 * headers).
 *
 * @param date  - The moment to label. Strings are parsed as ISO-8601.
 * @param now   - "Current" reference date; defaults to `new Date()`. Injected for testability.
 */
export const formatFriendlyTimestamp = (date: Date | string, now: Date = new Date()): string => {
  const parsed = typeof date === 'string' ? Dfns.parseISO(date) : date;
  const daysDiff = calendarDaysAgo(parsed, now);
  const time = Dfns.format('h:mm a', parsed);
  if (daysDiff === 0) return `Today, ${time}`;
  if (daysDiff === 1) return `Yesterday, ${time}`;
  const sameYear = Dfns.getYear(parsed) === Dfns.getYear(now);
  return `${Dfns.format(sameYear ? 'MMM d' : 'MMM d, yyyy', parsed)}, ${time}`;
};

/**
 * Splits a given date range into an array of formatted date strings representing the start of each week within the range.
 * The range is inclusive of both the start and end dates.
 *
 * @param {DateRange} dateRange - The range of dates to be split into weeks.
 * @returns {string[]} An array of formatted date strings, each representing the start of a week within the date range.
 */
export const getWeeksFromRange = (dateRange: DateRange): string[] => {
  const weeks: string[] = [];
  let currentWeek = dateRange.start;
  while (currentWeek <= dateRange.end) {
    weeks.push(currentWeek);
    currentWeek = Dfns.format(DATE_FORMAT, Dfns.addWeeks(1, Dfns.parseISO(currentWeek)));
  }

  return weeks;
};

/**
 * Checks if the provided date is a valid Date object.
 *
 * @param {Date} date - The date to validate.
 * @returns {boolean} If it's a valid date or not
 */
export const validDate = (date: Date): boolean => {
  if (date == null) {
    return false;
  }
  const timeNow = new Date(date);
  return timeNow != null && timeNow.getDate() != null && !Number.isNaN(timeNow.getDate());
};

/**
 * Returns a formatted date string representing the date exactly N days prior to today.
 *
 * @param {number} [daysAgo=1] - The number of days ago to subtract from the current date (optional, defaults to 1).
 * @param {string} [format=DATE_FORMAT] - The format in which to return the date (optional, defaults to the predefined DATE_FORMAT).
 * @returns {string} The formatted date string of the specified days ago.
 */
export const getFormattedDaysAgo = (daysAgo = 1, format = DATE_FORMAT): string =>
  Dfns.format(format, Dfns.subDays(daysAgo, new Date()));

/**
 * Returns a formatted date string representing the date exactly N months prior to today.
 *
 * @param {number} [monthsAgo=1] - The number of months ago to subtract from the current date (optional, defaults to 1).
 * @param {string} [format=DATE_FORMAT] - The format in which to return the date (optional, defaults to the predefined DATE_FORMAT).
 * @returns {string} The formatted date string of the specified months ago.
 */
export const getFormattedMonthsAgo = (monthsAgo = 1, format = DATE_FORMAT): string =>
  Dfns.format(format, Dfns.subMonths(monthsAgo, new Date()));

export const formatDateLabel = (
  date: string,
  fiscalCal: CompanyFiscalCal,
  groupingKey = 'Day',
  isTooltip = false,
  customFormatMap: Partial<Record<DateGroupingKey, string>> | undefined = undefined
): string => {
  if (!date || !Dfns.isValid(Dfns.parseISO(date))) {
    return '';
  }
  const { calType, seedDate, generatedPatternCal } = fiscalCal || {};

  const parsedDate = Dfns.parseISO(date);

  let dateToUse = parsedDate;

  let format: string;
  const customFormat =
    customFormatMap && customFormatMap[groupingKey] ? customFormatMap[groupingKey] : null;
  switch (groupingKey) {
    case 'Day':
      format = isTooltip ? DAY_DATE_TOOLTIP_LABEL : DAY_DATE_LABEL;
      break;
    case 'Week':
      format = isTooltip ? DAY_DATE_TOOLTIP_LABEL : WEEK_DATE_LABEL;
      break;
    case 'Month':
      switch (calType) {
        case FiscalCalType.PATTERN: {
          dateToUse = getMonthLabelPattern(date, generatedPatternCal as FiscalPatternCal);
          break;
        }
        default:
          break;
      }
      format = isTooltip ? MONTH_DATE_TOOLTIP_LABEL : MONTH_DATE_LABEL;
      break;
    case 'Quarter': {
      switch (calType) {
        case FiscalCalType.YEARLY: {
          dateToUse = getQuarterLabelYearly(date, seedDate as string);
          break;
        }
        case FiscalCalType.PATTERN: {
          dateToUse = getQuarterLabelPattern(date, generatedPatternCal as FiscalPatternCal);
          break;
        }
        default:
          break;
      }
      format = QUARTER_DATE_LABEL;
      break;
    }
    case 'Year': {
      switch (calType) {
        case FiscalCalType.YEARLY: {
          dateToUse = getYearLabelYearly(date, seedDate as string);
          break;
        }
        case FiscalCalType.PATTERN: {
          dateToUse = getYearLabelPattern(date, generatedPatternCal as FiscalPatternCal);
          break;
        }
        default:
          break;
      }
      format = YEAR_DATE_LABEL;
      break;
    }
    default:
      format = DAY_DATE_LABEL;
  }
  if (customFormat) {
    format = customFormat;
  }

  const formattedDate = Dfns.format(format, dateToUse);

  if (groupingKey === 'Week') {
    return `w/o ${formattedDate}`;
  }

  return formattedDate;
};

export const dateToMddyy = (date: string): string => {
  if (!date || !Dfns.isValid(Dfns.parseISO(date))) {
    return '';
  }
  return Dfns.format('M/dd/yy', Dfns.parseISO(date));
};

/**
 * Formats a UTC instant as `yyyy-MM-dd HH:mm UTC`, e.g. `2026-06-20 18:42 UTC`.
 * Not localized: a midnight-UTC instant would read as the previous day in the US.
 */
export const instantToUtcTimestamp = (instant: number | string): string =>
  `${new Date(instant).toISOString().slice(0, 16).replace('T', ' ')} UTC`;

/**
 * Formats an ISO date string (yyyy-MM-dd) as a long, human-readable date,
 * e.g. "January 5, 2024".
 */
export const dateToLongDate = (date: string): string => {
  if (!date || !Dfns.isValid(Dfns.parseISO(date))) {
    return '';
  }
  return Dfns.format('MMMM d, yyyy', Dfns.parseISO(date));
};

export const parseEpochDate = (dateNum: number): BpmDateString => {
  const dateObj = new Date(dateNum);
  const utcDateObj = convertDateToUTC(dateObj);
  return Dfns.format('yyyy-MM-dd', utcDateObj) as BpmDateString;
};

export const dateToMddyyDashed = (date: string): string => {
  if (!date || !Dfns.isValid(Dfns.parseISO(date))) {
    return '';
  }
  return Dfns.format('M-dd-yy', Dfns.parseISO(date));
};

/**
 * Gets the current timezone abbreviation (e.g., "PST", "EST", "UTC")
 * @returns {string} The timezone abbreviation
 */
export const getTimeZoneAbbr = (): string => {
  const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
  return (
    new Intl.DateTimeFormat('en', {
      timeZone,
      timeZoneName: 'short'
    })
      .formatToParts(new Date())
      .find(part => part.type === 'timeZoneName')?.value || ''
  );
};

/**
 * Generates time options in 12-hour format with 1 hour intervals
 * @returns {Array<ValueLabelRecord>} Array of time options
 */
export const generateTimeOptions = (): ValueLabelRecord[] => {
  const timeOptions: ValueLabelRecord[] = [];

  for (let hour = 0; hour <= 23; hour++) {
    const timeStr = `${hour % 12 || 12}:00${hour < 12 ? 'am' : 'pm'}`;
    timeOptions.push({ value: timeStr, label: timeStr });
  }

  return timeOptions;
};

export const parseBase64GzippedResponse = async <T>(res: Response): Promise<T> => {
  const responseText = await res.text();
  let responseStringBase64: string;
  try {
    responseStringBase64 = JSON.parse(responseText);
  } catch {
    // Some lambdas return the base64 body unquoted.
    responseStringBase64 = responseText;
  }
  const compressedData = base64ToUint8Array(responseStringBase64);
  const unCompressedJsonString = ungzip(compressedData, { toText: true });
  const data = JSON.parse(unCompressedJsonString);

  return data;
};
